<script setup lang="ts">
import type { ScrubbedQuestion } from '@ilteducation/quizter-types';
import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useQuizterStore } from '../stores/quizterStore';
import AnswerCard from './AnswerCard.vue';
import { GameState } from '../util';
import QuestionPhrase from './QuestionPhrase.vue';

const { timeLeft, gameOptions, questionCountdown, state } = storeToRefs(useQuizterStore());

type Props = {
  currentQuestion: ScrubbedQuestion;
  currentQuestionIndex: number;
  numberOfQuestions?: number;
  selectedAnswerId?: string | null;
  showTimer?: boolean;
  host?: boolean;
  highlightCorrectAnswer?: boolean;
  currentQuestionAnswer?: string | null;
  currentQuestionAnswers?: Record<string, number>; //In percentage
};

type Emits = {
  (e: 'answer-submitted', answerId: string, questionId: string): void;
};

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const showAlternatives = computed(() => questionCountdown.value <= 0);

const handleAnswerSelected = (answerId: string): void => {
  if (!props.currentQuestion.id || (gameOptions.value.useTimer && timeLeft.value <= 0)) {
    return;
  }
  emit('answer-submitted', answerId, props.currentQuestion.id);
};

const isAnswerCardDisabled = (answerId: string): boolean => {
  return (
    (!!props.selectedAnswerId && props.selectedAnswerId !== answerId) ||
    (props.highlightCorrectAnswer && answerId !== props.currentQuestionAnswer) ||
    (timeLeft.value <= 0 && state.value === GameState.SHOW_QUESTIONS && gameOptions.value.useTimer)
  );
};
</script>

<template>
  <div v-if="currentQuestion" :class="{ host: host }">
    <QuestionPhrase :current-question="currentQuestion" :host="host" :hide-phrase-timer="showAlternatives" />
    <!-- Timer and alternatives (shown after countdown) -->
    <template v-if="showAlternatives">
      <v-progress-linear
        v-if="showTimer"
        class="countdown-bar rounded mb-10"
        :model-value="timeLeft - 200"
        :max="gameOptions.questionAnswerTimeout"
        height="4"
        aria-hidden="true"
      ></v-progress-linear>
      <div v-else class="mb-10"></div>
      <div class="answer-card-grid">
        <AnswerCard
          :key="opt.id"
          class="answer-card"
          v-for="(opt, index) in currentQuestion.options"
          :option="opt.value"
          :index="index"
          :selected="selectedAnswerId === opt.id"
          :disabled="isAnswerCardDisabled(opt.id)"
          :wrong-answer="highlightCorrectAnswer && opt.id !== currentQuestionAnswer"
          :is-correct-answer="highlightCorrectAnswer && opt.id === currentQuestionAnswer"
          :percentage="highlightCorrectAnswer && currentQuestionAnswers ? currentQuestionAnswers[opt.id] : undefined"
          @pointerup="() => handleAnswerSelected(opt.id)"
          @keydown.enter.prevent.stop="() => handleAnswerSelected(opt.id)"
          @keydown.space.prevent.stop="() => handleAnswerSelected(opt.id)"
          role="button"
          tabindex="0"
          :aria-label="
            highlightCorrectAnswer && opt.id === currentQuestionAnswer ? `Correct answer. ${opt.value}` : opt.value
          "
          :aria-pressed="selectedAnswerId === opt.id"
          :aria-disabled="isAnswerCardDisabled(opt.id)"
        ></AnswerCard>
      </div>
    </template>
  </div>
</template>
<style scoped lang="scss">
@use 'sass:map';
@use 'vuetify/settings' as v;

.phrase {
  font-size: 1.5rem;
  text-align: center;
}

.host .phrase {
  font-size: 2.5rem;
}

.host .answer-card-grid {
  zoom: 0.85;
}

:deep(.countdown-bar .v-progress-linear__determinate) {
  transition: none !important;
}

.answer-card-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 40px;
}

.answer-card {
  grid-column: span 4;
}

@media #{map.get(v.$display-breakpoints, 'sm-and-up')} {
  .answer-card {
    grid-column: span 2;

    &:nth-child(3):last-child {
      grid-column: 2 / span 2;
    }
  }
}
</style>
